import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { useCompany } from "../context/CompanyContext";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Plus, Trash, PencilSimple, FloppyDisk, Buildings, Users, Bell, Check, CalendarBlank, ClockCounterClockwise, ArrowRight, Key, ListChecks, CaretRight, Sparkle, HandWaving } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import QboCard from "../components/QboCard"; // eslint-disable-line no-unused-vars

import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

export default function Settings() {
  const { activeCompanyId, activeCompany, companies, reloadCompanies } = useCompany();
  const { user, checkSession } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [newItem, setNewItem] = useState({ name: "", vendor: "", category: "statement" });
  const [addOpen, setAddOpen] = useState(false);
  const [companyOpen, setCompanyOpen] = useState(false);
  const [newCompany, setNewCompany] = useState("");
  const [inviteOpen, setInviteOpen] = useState(false);
  const [invite, setInvite] = useState({ name: "", email: "", password: "", role: "staff", company_ids: [] });
  const [users, setUsers] = useState([]);
  const [pwdOpen, setPwdOpen] = useState(false);
  const [pwd, setPwd] = useState({ current: "", next: "", confirm: "" });
  const [pwdLoading, setPwdLoading] = useState(false);

  const loadTemplates = useCallback(async () => {
    if (!activeCompanyId) return;
    const { data } = await api.get(`/recurring-templates?company_id=${activeCompanyId}`);
    setTemplates(data);
  }, [activeCompanyId]);

  const loadUsers = useCallback(async () => {
    if (user?.role !== "owner") return;
    try {
      const { data } = await api.get("/users");
      setUsers(data);
    } catch (e) {
      console.error("Failed to load users:", e);
      toast.error("Couldn't load the user list. Please refresh.");
    }
  }, [user]);

  useEffect(() => { loadTemplates(); }, [loadTemplates]);
  useEffect(() => { loadUsers(); }, [loadUsers]);

  const handleAddTemplate = async () => {
    if (!newItem.name.trim()) return;
    try {
      await api.post("/recurring-templates", {
        company_id: activeCompanyId,
        name: newItem.name.trim(),
        vendor: newItem.vendor.trim() || null,
        category: newItem.category,
      });
      toast.success("Added recurring item");
      setNewItem({ name: "", vendor: "", category: "statement" });
      setAddOpen(false);
      await loadTemplates();
    } catch (e) {
      toast.error("Failed to add");
    }
  };

  const handleToggleActive = async (t) => {
    try {
      await api.patch(`/recurring-templates/${t.id}`, { active: !t.active });
      await loadTemplates();
    } catch (e) { toast.error("Failed"); }
  };

  const handleDeleteTemplate = async (id) => {
    if (!confirm("Delete this recurring template? (Existing monthly items remain.)")) return;
    try {
      await api.delete(`/recurring-templates/${id}`);
      await loadTemplates();
    } catch (e) { toast.error("Failed"); }
  };

  const handleAddCompany = async () => {
    if (!newCompany.trim()) return;
    try {
      await api.post("/companies", { name: newCompany.trim() });
      toast.success("Company added");
      setNewCompany("");
      setCompanyOpen(false);
      await reloadCompanies();
    } catch (e) { toast.error("Failed"); }
  };

  const handleInvite = async () => {
    if (!invite.email || !invite.password || !invite.name) return;
    try {
      await api.post("/auth/register", {
        ...invite,
        company_ids: invite.company_ids.length ? invite.company_ids : companies.map((c) => c.id),
      });
      toast.success(`Invited ${invite.name}`);
      setInvite({ name: "", email: "", password: "", role: "staff", company_ids: [] });
      setInviteOpen(false);
      await loadUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to invite");
    }
  };

  const updateNotifPref = async (pref) => {
    try {
      const fd = new FormData();
      fd.append("pref", pref);
      await api.patch("/users/me/notification-pref", fd);
      toast.success("Preference saved");
      await checkSession();
    } catch (e) { toast.error("Failed"); }
  };

  const submitPasswordChange = async () => {
    if (pwd.next.length < 8) {
      toast.error("New password must be at least 8 characters");
      return;
    }
    if (pwd.next !== pwd.confirm) {
      toast.error("New passwords don't match");
      return;
    }
    setPwdLoading(true);
    try {
      await api.post("/auth/change-password", {
        current_password: pwd.current,
        new_password: pwd.next,
      });
      toast.success("Password changed");
      setPwdOpen(false);
      setPwd({ current: "", next: "", confirm: "" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to change password");
    }
    setPwdLoading(false);
  };

  return (
    <div data-testid="settings-page" className="p-6 sm:p-8 max-w-[1200px] space-y-10">
      <div>
        <div className="label-tag mb-2">Settings</div>
        <h1 className="font-heading text-4xl sm:text-5xl font-medium tracking-tight leading-none">
          Configure your workspace.
        </h1>
      </div>

      {/* Quick start / how-to — surfaced here so the More tab on mobile (which
          lands on Settings) has a clear path to the onboarding tour. */}
      <Link
        to="/welcome"
        data-testid="settings-quick-start-link"
        className="surface-card bg-white p-5 flex items-center gap-4 hover:border-[#0A0A0A] transition-all"
      >
        <div className="w-10 h-10 bg-[#002FA7]/10 flex items-center justify-center flex-shrink-0">
          <HandWaving size={20} weight="duotone" className="text-[#002FA7]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-heading font-semibold">Quick start tour</div>
          <div className="text-xs text-[#525252] mt-0.5">New to the portal? Take the 60-second tour for your role.</div>
        </div>
        <CaretRight size={16} weight="bold" className="text-[#A3A3A3] flex-shrink-0" />
      </Link>

      {/* Reports & audit */}
      <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Link
          to="/history"
          data-testid="link-history"
          className="surface-card bg-white p-5 flex items-center gap-4 hover:border-[#0A0A0A] transition-all"
        >
          <div className="w-10 h-10 bg-[#002FA7]/10 flex items-center justify-center">
            <CalendarBlank size={20} weight="duotone" className="text-[#002FA7]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-heading font-medium">Year view</div>
            <div className="text-xs text-[#525252]">12-month grid + annual export (ZIP)</div>
          </div>
          <ArrowRight size={16} weight="bold" className="text-[#525252]" />
        </Link>
        <Link
          to="/activity"
          data-testid="link-activity"
          className="surface-card bg-white p-5 flex items-center gap-4 hover:border-[#0A0A0A] transition-all"
        >
          <div className="w-10 h-10 bg-[#002FA7]/10 flex items-center justify-center">
            <ClockCounterClockwise size={20} weight="duotone" className="text-[#002FA7]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-heading font-medium">Activity log</div>
            <div className="text-xs text-[#525252]">Audit trail of every change</div>
          </div>
          <ArrowRight size={16} weight="bold" className="text-[#525252]" />
        </Link>
      </section>

      {/* Notifications — hidden until email digest delivery is wired up
      <section className="surface-card bg-white">
        <div className="p-5 border-b border-[#E5E5E5] flex items-center gap-3">
          <Bell size={20} weight="duotone" className="text-[#002FA7]" />
          <div>
            <h2 className="font-heading text-lg font-medium">Email digest</h2>
            <p className="text-xs text-[#525252]">How often do you want email updates about pending items?</p>
          </div>
        </div>
        <div className="p-5 grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { v: "off", label: "None" },
            { v: "weekly", label: "Weekly" },
            { v: "month_end", label: "Month-end" },
            { v: "immediate", label: "Immediate" },
          ].map((opt) => {
            const selected = user?.notification_pref === opt.v;
            return (
              <button
                key={opt.v}
                onClick={() => updateNotifPref(opt.v)}
                data-testid={`notif-${opt.v}`}
                className={`p-4 border text-left transition-all ${
                  selected
                    ? "border-[#002FA7] bg-[#002FA7]/5"
                    : "border-[#E5E5E5] hover:border-[#0A0A0A]"
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="font-heading font-medium">{opt.label}</div>
                  {selected && <Check size={16} weight="bold" className="text-[#002FA7]" />}
                </div>
              </button>
            );
          })}
        </div>
      </section>
      */}

      {/* Account / Security */}
      <section className="surface-card bg-white" data-testid="account-security-section">
        <div className="p-5 border-b border-[#E5E5E5] flex items-center gap-3">
          <Key size={20} weight="duotone" className="text-[#002FA7]" />
          <div>
            <h2 className="font-heading text-lg font-medium">Account &amp; security</h2>
            <p className="text-xs text-[#525252]">Signed in as {user?.email} ({user?.role})</p>
          </div>
        </div>
        <div className="p-5 flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="font-medium text-sm">Password</div>
            <div className="text-xs text-[#525252] mt-0.5">Change your portal password at any time.</div>
          </div>
          <Dialog open={pwdOpen} onOpenChange={(v) => { setPwdOpen(v); if (!v) setPwd({ current: "", next: "", confirm: "" }); }}>
            <DialogTrigger asChild>
              <button
                className="btn-ikb px-3 py-2 text-sm flex items-center gap-1.5"
                data-testid="change-password-btn"
              >
                <Key size={14} weight="bold" /> Change password
              </button>
            </DialogTrigger>
            <DialogContent className="rounded-none" data-testid="change-password-dialog">
              <DialogHeader>
                <DialogTitle className="font-heading">Change password</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-2">
                <div>
                  <label className="label-tag block mb-2">Current password</label>
                  <input
                    type="password"
                    value={pwd.current}
                    onChange={(e) => setPwd({ ...pwd, current: e.target.value })}
                    className="w-full px-3 py-2.5 border border-[#E5E5E5] focus:outline-none focus:border-[#002FA7]"
                    autoComplete="current-password"
                    data-testid="pwd-current"
                    autoFocus
                  />
                </div>
                <div>
                  <label className="label-tag block mb-2">New password (min 8 chars)</label>
                  <input
                    type="password"
                    value={pwd.next}
                    onChange={(e) => setPwd({ ...pwd, next: e.target.value })}
                    className="w-full px-3 py-2.5 border border-[#E5E5E5] focus:outline-none focus:border-[#002FA7]"
                    autoComplete="new-password"
                    data-testid="pwd-new"
                  />
                </div>
                <div>
                  <label className="label-tag block mb-2">Confirm new password</label>
                  <input
                    type="password"
                    value={pwd.confirm}
                    onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })}
                    className="w-full px-3 py-2.5 border border-[#E5E5E5] focus:outline-none focus:border-[#002FA7]"
                    autoComplete="new-password"
                    data-testid="pwd-confirm"
                    onKeyDown={(e) => { if (e.key === "Enter" && pwd.current && pwd.next && pwd.confirm) submitPasswordChange(); }}
                  />
                </div>
              </div>
              <DialogFooter>
                <button
                  onClick={() => setPwdOpen(false)}
                  className="px-4 py-2 border border-[#E5E5E5]"
                  data-testid="pwd-cancel"
                >
                  Cancel
                </button>
                <button
                  onClick={submitPasswordChange}
                  disabled={pwdLoading || !pwd.current || !pwd.next || !pwd.confirm}
                  className="btn-ikb px-4 py-2 disabled:opacity-60"
                  data-testid="pwd-submit"
                >
                  {pwdLoading ? "Saving…" : "Change password"}
                </button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </section>

      {/* Companies (owner only) */}
      {user?.role === "owner" && (
        <section className="surface-card bg-white">
          <div className="p-5 border-b border-[#E5E5E5] flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Buildings size={20} weight="duotone" className="text-[#002FA7]" />
              <div>
                <h2 className="font-heading text-lg font-medium">Entities</h2>
                <p className="text-xs text-[#525252]">Separate workspace for each business entity you own.</p>
              </div>
            </div>
            <Dialog open={companyOpen} onOpenChange={setCompanyOpen}>
              <DialogTrigger asChild>
                <button className="btn-ikb px-3 py-1.5 text-xs flex items-center gap-1.5" data-testid="add-company-button">
                  <Plus size={14} weight="bold" /> Add entity
                </button>
              </DialogTrigger>
              <DialogContent className="rounded-none">
                <DialogHeader><DialogTitle className="font-heading">New entity</DialogTitle></DialogHeader>
                <input
                  value={newCompany}
                  onChange={(e) => setNewCompany(e.target.value)}
                  placeholder="Legal entity name"
                  className="w-full px-3 py-2 border border-[#E5E5E5] focus:border-[#002FA7] focus:outline-none"
                  data-testid="new-company-name"
                />
                <DialogFooter>
                  <button onClick={() => setCompanyOpen(false)} className="px-4 py-2 border border-[#E5E5E5]">Cancel</button>
                  <button onClick={handleAddCompany} className="btn-ikb px-4 py-2" data-testid="submit-new-company">Add</button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
          <div className="divide-y divide-[#E5E5E5]">
            {companies.map((c) => (
              <div key={c.id} className="p-4 flex items-center justify-between" data-testid={`company-row-${c.id}`}>
                <div className="font-heading text-sm font-medium">{c.name}</div>
                <div className="label-tag text-[10px]">id: {c.id.slice(0, 8)}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Recurring templates */}
      <section className="surface-card bg-white">
        <div className="p-5 border-b border-[#E5E5E5] flex items-center justify-between flex-wrap gap-3">
          <div>
            <h2 className="font-heading text-lg font-medium">Recurring templates</h2>
            <p className="text-xs text-[#525252]">
              {activeCompany?.name} — templates generate the monthly items automatically.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Dialog open={addOpen} onOpenChange={setAddOpen}>
              <DialogTrigger asChild>
                <button className="btn-ikb px-3 py-1.5 text-xs flex items-center gap-1.5" data-testid="add-template-button">
                  <Plus size={14} weight="bold" /> Add
                </button>
              </DialogTrigger>
            <DialogContent className="rounded-none">
              <DialogHeader><DialogTitle className="font-heading">New recurring template</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div>
                  <div className="label-tag mb-1">Name</div>
                  <input
                    value={newItem.name}
                    onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
                    placeholder="e.g. Capital One Business Statement"
                    className="w-full px-3 py-2 border border-[#E5E5E5] focus:border-[#002FA7] focus:outline-none"
                    data-testid="new-template-name"
                  />
                </div>
                <div>
                  <div className="label-tag mb-1">Vendor (optional)</div>
                  <input
                    value={newItem.vendor}
                    onChange={(e) => setNewItem({ ...newItem, vendor: e.target.value })}
                    placeholder="e.g. Capital One"
                    className="w-full px-3 py-2 border border-[#E5E5E5] focus:border-[#002FA7] focus:outline-none"
                    data-testid="new-template-vendor"
                  />
                </div>
                <div>
                  <div className="label-tag mb-1">Category</div>
                  <Select value={newItem.category} onValueChange={(v) => setNewItem({ ...newItem, category: v })}>
                    <SelectTrigger className="rounded-none" data-testid="new-template-category"><SelectValue /></SelectTrigger>
                    <SelectContent className="rounded-none">
                      <SelectItem className="rounded-none" value="statement">Statement</SelectItem>
                      <SelectItem className="rounded-none" value="loan">Loan / Lease</SelectItem>
                      <SelectItem className="rounded-none" value="vendor">Vendor account</SelectItem>
                      <SelectItem className="rounded-none" value="invoice">Invoice</SelectItem>
                      <SelectItem className="rounded-none" value="decision">Decision (pay / skip)</SelectItem>
                      <SelectItem className="rounded-none" value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <button onClick={() => setAddOpen(false)} className="px-4 py-2 border border-[#E5E5E5]">Cancel</button>
                <button onClick={handleAddTemplate} className="btn-ikb px-4 py-2" data-testid="submit-new-template">Add template</button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          </div>
        </div>
        <div className="divide-y divide-[#E5E5E5]">
          {templates.length === 0 && <div className="p-5 text-sm text-[#525252]">No templates yet.</div>}
          {templates.map((t) => (
            <div key={t.id} data-testid={`template-${t.id}`} className="p-4 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className={`font-medium text-sm ${!t.active ? "text-[#525252] line-through" : ""}`}>{t.name}</div>
                <div className="label-tag text-[10px]">{t.category} {t.vendor ? `· ${t.vendor}` : ""}</div>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => handleToggleActive(t)}
                  className={`px-2.5 py-1 text-[10px] tracking-wider uppercase font-semibold transition-all ${
                    t.active ? "bg-[#10B981]/10 text-[#10B981]" : "bg-[#525252]/10 text-[#525252]"
                  }`}
                  data-testid={`toggle-template-${t.id}`}
                >
                  {t.active ? "Active" : "Paused"}
                </button>
                <button
                  onClick={() => handleDeleteTemplate(t.id)}
                  className="p-1.5 hover:bg-[#F5F5F5] text-[#525252] transition-all"
                  data-testid={`delete-template-${t.id}`}
                >
                  <Trash size={14} weight="duotone" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Users (owner only) */}
      {user?.role === "owner" && (
        <section className="surface-card bg-white">
          <div className="p-5 border-b border-[#E5E5E5] flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Users size={20} weight="duotone" className="text-[#002FA7]" />
              <div>
                <h2 className="font-heading text-lg font-medium">Team</h2>
                <p className="text-xs text-[#525252]">Grant your bookkeeper and staff access to the portal.</p>
              </div>
            </div>
            <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
              <DialogTrigger asChild>
                <button className="btn-ikb px-3 py-1.5 text-xs flex items-center gap-1.5" data-testid="invite-user-button">
                  <Plus size={14} weight="bold" /> Add team member
                </button>
              </DialogTrigger>
              <DialogContent className="rounded-none max-w-lg">
                <DialogHeader><DialogTitle className="font-heading">Invite team member</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div>
                    <div className="label-tag mb-1">Name</div>
                    <input
                      value={invite.name}
                      onChange={(e) => setInvite({ ...invite, name: e.target.value })}
                      className="w-full px-3 py-2 border border-[#E5E5E5] focus:border-[#002FA7] focus:outline-none"
                      data-testid="invite-name"
                    />
                  </div>
                  <div>
                    <div className="label-tag mb-1">Email</div>
                    <input
                      type="email"
                      value={invite.email}
                      onChange={(e) => setInvite({ ...invite, email: e.target.value })}
                      className="w-full px-3 py-2 border border-[#E5E5E5] focus:border-[#002FA7] focus:outline-none"
                      data-testid="invite-email"
                    />
                  </div>
                  <div>
                    <div className="label-tag mb-1">Temporary password</div>
                    <input
                      type="text"
                      value={invite.password}
                      onChange={(e) => setInvite({ ...invite, password: e.target.value })}
                      className="w-full px-3 py-2 border border-[#E5E5E5] focus:border-[#002FA7] focus:outline-none"
                      data-testid="invite-password"
                    />
                  </div>
                  <div>
                    <div className="label-tag mb-1">Role</div>
                    <Select value={invite.role} onValueChange={(v) => setInvite({ ...invite, role: v })}>
                      <SelectTrigger className="rounded-none" data-testid="invite-role"><SelectValue /></SelectTrigger>
                      <SelectContent className="rounded-none">
                        <SelectItem className="rounded-none" value="bookkeeper">Bookkeeper</SelectItem>
                        <SelectItem className="rounded-none" value="staff">Staff</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <DialogFooter>
                  <button onClick={() => setInviteOpen(false)} className="px-4 py-2 border border-[#E5E5E5]">Cancel</button>
                  <button onClick={handleInvite} className="btn-ikb px-4 py-2" data-testid="submit-invite">Send invite</button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
          <div className="divide-y divide-[#E5E5E5]">
            {users.map((u) => {
              const isSelf = u.id === user?.id;
              return (
                <div key={u.id} className="p-4 flex items-center justify-between gap-3" data-testid={`user-${u.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-sm truncate">{u.name} {isSelf && <span className="text-[10px] text-[#525252] ml-1">(you)</span>}</div>
                    <div className="text-xs text-[#525252] truncate">{u.email}</div>
                  </div>
                  <span className="label-tag text-[10px] bg-[#F5F5F5] px-2 py-1 flex-shrink-0">{u.role}</span>
                  {!isSelf && (
                    <button
                      onClick={async () => {
                        const newPw = window.prompt(
                          `Set a new password for ${u.name} (${u.email}).\n\nMin 8 characters. After you click OK, share the new password with them — they should change it on first login (Settings → Change password).`,
                          ""
                        );
                        if (newPw == null) return; // cancelled
                        if (newPw.length < 8) {
                          toast.error("Password must be at least 8 characters");
                          return;
                        }
                        try {
                          await api.post(`/users/${u.id}/reset-password`, { new_password: newPw });
                          // Use prompt to show because toasts get dismissed and the
                          // owner needs to see/copy the password they just set.
                          window.alert(`Password reset for ${u.email}.\n\nNew password:\n${newPw}\n\nSend this to them via a secure channel (text the password, email the login URL — keep them separate).`);
                        } catch (e) {
                          toast.error(e?.response?.data?.detail || "Failed to reset password");
                        }
                      }}
                      title="Reset password for this user"
                      className="text-[#525252] hover:text-[#002FA7] p-2 transition-colors flex-shrink-0"
                      data-testid={`reset-password-${u.id}`}
                    >
                      <Key size={16} weight="duotone" />
                    </button>
                  )}
                  {!isSelf && (
                    <button
                      onClick={async () => {
                        if (!window.confirm(`Remove ${u.name} (${u.email})? They will immediately lose access.`)) return;
                        try {
                          await api.delete(`/users/${u.id}`);
                          toast.success("User removed");
                          loadUsers();
                        } catch (e) {
                          toast.error(e?.response?.data?.detail || "Failed to remove user");
                        }
                      }}
                      title="Remove user"
                      className="text-[#525252] hover:text-[#DC2626] p-2 transition-colors flex-shrink-0"
                      data-testid={`delete-user-${u.id}`}
                    >
                      <Trash size={16} weight="duotone" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Portal adoption tracking — owner-only, NOT visible to bookkeeper/staff */}
      {user?.role === "owner" && <CloseTargetCard />}
      {user?.role === "owner" && <InboundEmailCard />}
    </div>
  );
}

// Close target day — per-company, per-month-end target. Owner only.
function CloseTargetCard() {
  const { activeCompanyId, activeCompany, reloadCompanies } = useCompany();
  const [day, setDay] = useState(activeCompany?.close_target_day || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setDay(activeCompany?.close_target_day || "");
  }, [activeCompany?.close_target_day, activeCompanyId]);

  const save = async () => {
    setSaving(true);
    try {
      const v = day === "" ? 0 : Number(day);
      if (v !== 0 && (v < 1 || v > 31)) {
        toast.error("Pick a day between 1 and 31, or clear it.");
        setSaving(false);
        return;
      }
      await api.patch(`/companies/${activeCompanyId}/settings`, { close_target_day: v });
      toast.success(v === 0 ? "Close target cleared" : `Books should be closed by the ${v}th of next month`);
      await reloadCompanies?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="surface-card bg-white p-6 mb-6" data-testid="close-target-card">
      <div className="mb-4">
        <div className="label-tag mb-1">Cadence</div>
        <h2 className="font-heading text-lg font-medium">Close target day</h2>
        <p className="text-sm text-[#525252] mt-1">
          When books for a given month should be closed (the day of the <em>following</em> month).
          Shows as a soft progress bar on the closing-period section of the dashboard — gradually shifts blue → amber → red as the date approaches.
          Leave blank to hide the indicator.
        </p>
      </div>
      <div className="flex items-center gap-3">
        <span className="text-sm text-[#525252]">By the</span>
        <input
          type="number"
          min={1}
          max={31}
          value={day}
          onChange={(e) => setDay(e.target.value)}
          placeholder="—"
          className="w-20 px-3 py-2 border border-[#E5E5E5] focus:border-[#002FA7] focus:outline-none text-center"
          data-testid="close-target-day-input"
        />
        <span className="text-sm text-[#525252]">of the next month</span>
        <button
          onClick={save}
          disabled={saving}
          className="ml-auto btn-ikb px-4 py-2 text-sm"
          data-testid="close-target-save"
        >
          {saving ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
}

// Bank workflow — choose how Payments land in QBO. Direct-to-bank (one check =
// one deposit) is the default for users who don't batch deposits. Owner only.
function BankWorkflowCard() {
  const { activeCompanyId, activeCompany, reloadCompanies } = useCompany();
  const [accounts, setAccounts] = useState([]);
  const [acctId, setAcctId] = useState(activeCompany?.default_deposit_account_id || "");
  const [showAdvanced, setShowAdvanced] = useState(!!activeCompany?.show_advanced_bank_tools);
  const [confirmPeriod, setConfirmPeriod] = useState(!!activeCompany?.confirm_period_correction);
  const [saving, setSaving] = useState(false);
  const [loadErr, setLoadErr] = useState("");

  useEffect(() => {
    setAcctId(activeCompany?.default_deposit_account_id || "");
    setShowAdvanced(!!activeCompany?.show_advanced_bank_tools);
    setConfirmPeriod(!!activeCompany?.confirm_period_correction);
  }, [activeCompany?.default_deposit_account_id, activeCompany?.show_advanced_bank_tools, activeCompany?.confirm_period_correction, activeCompanyId]);

  useEffect(() => {
    if (!activeCompanyId) return;
    setLoadErr("");
    api.get(`/qbo/bank-accounts?company_id=${activeCompanyId}`)
      .then(({ data }) => setAccounts(data.accounts || []))
      .catch((e) => {
        setAccounts([]);
        setLoadErr(e?.response?.data?.detail || "Connect QuickBooks to load bank accounts.");
      });
  }, [activeCompanyId]);

  const save = async () => {
    setSaving(true);
    try {
      const acctName = accounts.find((a) => a.id === acctId)?.name || "";
      await api.patch(`/companies/${activeCompanyId}/settings`, {
        default_deposit_account_id: acctId || "",
        default_deposit_account_name: acctName || "",
        show_advanced_bank_tools: showAdvanced,
        confirm_period_correction: confirmPeriod,
      });
      toast.success(acctId ? `New payments will deposit to ${acctName}` : "Default deposit account cleared");
      await reloadCompanies?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="surface-card bg-white p-6 mb-6" data-testid="bank-workflow-card">
      <div className="mb-4">
        <div className="label-tag mb-1">Bank workflow</div>
        <h2 className="font-heading text-lg font-medium">How payments land in QuickBooks</h2>
        <p className="text-sm text-[#525252] mt-1">
          One check = one deposit (direct-to-bank). When you apply a remittance, the QuickBooks Payment is
          posted straight into the bank account you pick below — no Undeposited Funds, no batching. The bank feed
          in QuickBooks then matches it on its own.
        </p>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm text-[#525252] mb-1">Default deposit account</label>
          {loadErr ? (
            <div className="text-sm text-[#737373] italic">{loadErr}</div>
          ) : (
            <select
              value={acctId}
              onChange={(e) => setAcctId(e.target.value)}
              className="w-full max-w-md px-3 py-2 border border-[#E5E5E5] focus:border-[#002FA7] focus:outline-none bg-white"
              data-testid="default-deposit-account-select"
            >
              <option value="">— Use QuickBooks default (Undeposited Funds) —</option>
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>{a.name}</option>
              ))}
            </select>
          )}
          <p className="text-xs text-[#737373] mt-1">
            Tessina and Poly will see &ldquo;Deposits go to: {acctId ? (accounts.find((a) => a.id === acctId)?.name || "—") : "Undeposited Funds"}&rdquo; on the Remittances page.
          </p>
        </div>

        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={showAdvanced}
            onChange={(e) => setShowAdvanced(e.target.checked)}
            className="mt-1"
            data-testid="show-advanced-bank-tools-toggle"
          />
          <span className="text-sm">
            <span className="block font-medium text-[#171717]">Show advanced bank tools</span>
            <span className="block text-[#737373] text-xs mt-0.5">
              Reveals the older bank-statement upload, &ldquo;Sync bank clearance&rdquo;, and &ldquo;Re-create QBO Deposit&rdquo; buttons. Most users leave this off.
            </span>
          </span>
        </label>

        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={confirmPeriod}
            onChange={(e) => setConfirmPeriod(e.target.checked)}
            className="mt-1"
            data-testid="confirm-period-correction-toggle"
          />
          <span className="text-sm">
            <span className="block font-medium text-[#171717]">Confirm month before submitting statement uploads</span>
            <span className="block text-[#737373] text-xs mt-0.5">
              When the AI detects a statement covers a different month than the one you&apos;re viewing,
              show a confirmation prompt instead of auto-routing silently. Useful if you&apos;ve had the
              AI guess wrong even once.
            </span>
          </span>
        </label>

        <div className="pt-2">
          <button
            onClick={save}
            disabled={saving}
            className="btn-ikb px-4 py-2 text-sm"
            data-testid="bank-workflow-save"
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

// Inbound email forwarding (Postmark). Owner picks the per-company "slug"
// that gets appended to the Postmark hash via plus-addressing. Tessina/Zach
// then forward bookkeeping emails to that address; AI classifies each
// attachment as remittance or expense and creates a row.
function InboundEmailCard() {
  const { activeCompanyId, activeCompany, reloadCompanies } = useCompany();
  const [hash, setHash] = useState("");
  const [configured, setConfigured] = useState(false);
  const [envSenders, setEnvSenders] = useState([]);
  const [dbSenders, setDbSenders] = useState([]);
  const [slug, setSlug] = useState(activeCompany?.inbound_mailbox_hash || "");
  const [saving, setSaving] = useState(false);
  const [newSender, setNewSender] = useState("");
  const [adding, setAdding] = useState(false);

  useEffect(() => { setSlug(activeCompany?.inbound_mailbox_hash || ""); }, [activeCompany?.inbound_mailbox_hash]);

  const loadConfig = useCallback(() => {
    api.get("/inbound/config")
      .then(({ data }) => {
        setHash(data.postmark_inbound_hash || "");
        setConfigured(!!data.configured);
        setEnvSenders(data.env_allowed_senders || []);
        setDbSenders(data.db_allowed_senders || []);
      })
      .catch(() => {});
  }, []);
  useEffect(() => { loadConfig(); }, [loadConfig]);

  const fullAddress = hash && slug ? `${hash}+${slug}@inbound.postmarkapp.com` : "";

  const save = async () => {
    setSaving(true);
    try {
      const cleanSlug = (slug || "").trim().toLowerCase().replace(/[^a-z0-9-]/g, "");
      await api.patch(`/companies/${activeCompanyId}/settings`, { inbound_mailbox_hash: cleanSlug });
      toast.success(cleanSlug ? `Forwarding address active for ${activeCompany?.name}` : "Inbound forwarding cleared");
      await reloadCompanies?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const copy = async () => {
    if (!fullAddress) return;
    try { await navigator.clipboard.writeText(fullAddress); toast.success("Address copied"); }
    catch { toast.error("Couldn't copy — select the text manually"); }
  };

  const addSender = async () => {
    const email = newSender.trim().toLowerCase();
    if (!email || !email.includes("@")) { toast.error("Enter a valid email address"); return; }
    setAdding(true);
    try {
      await api.post("/inbound/allowed-senders", { email });
      setNewSender("");
      toast.success(`${email} can now forward emails to your inbound address`);
      loadConfig();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add");
    } finally { setAdding(false); }
  };

  const removeSender = async (email) => {
    if (!window.confirm(`Stop accepting forwards from ${email}?`)) return;
    try {
      await api.delete(`/inbound/allowed-senders/${encodeURIComponent(email)}`);
      toast.success("Removed");
      loadConfig();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="surface-card bg-white p-6 mb-6" data-testid="inbound-email-card">
      <div className="mb-4">
        <div className="label-tag mb-1">Inbound email</div>
        <h2 className="font-heading text-lg font-medium">Forward emails to create rows automatically</h2>
        <p className="text-sm text-[#525252] mt-1">
          Anyone on the allowed-senders list can forward a remittance email or vendor bill to the address
          below. AI reads each attachment and adds it to your Remittances or Expenses list — no upload needed.
        </p>
      </div>

      {!configured && (
        <div className="mb-4 p-3 bg-[#FEF3C7] border border-[#F59E0B]/40 text-xs text-[#92400E]">
          Postmark webhook isn&apos;t fully configured yet. Set <code>POSTMARK_INBOUND_HASH</code> and <code>POSTMARK_INBOUND_PASSWORD</code> in your backend env.
        </div>
      )}

      <div className="space-y-5">
        <div>
          <label className="block text-sm text-[#525252] mb-1">Mailbox slug for {activeCompany?.name || "this company"}</label>
          <div className="flex gap-2 flex-wrap items-stretch">
            <input
              type="text"
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              placeholder="northstar"
              className="px-3 py-2 border border-[#E5E5E5] focus:border-[#002FA7] focus:outline-none bg-white text-sm w-48"
              data-testid="inbound-slug-input"
            />
            <button onClick={save} disabled={saving} className="btn-ikb px-4 py-2 text-sm" data-testid="inbound-slug-save">
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
          <p className="text-xs text-[#737373] mt-1">
            Lowercase letters, numbers, and dashes. Empty clears the address (forwarding stops).
          </p>
        </div>

        {fullAddress && (
          <div className="p-3 bg-[#10B981]/5 border border-[#10B981]/30">
            <div className="text-[10px] font-bold uppercase tracking-wider text-[#047857] mb-1">Forwarding address</div>
            <div className="flex items-center gap-2 flex-wrap">
              <code className="text-sm font-mono break-all" data-testid="inbound-address-display">{fullAddress}</code>
              <button onClick={copy} className="text-xs text-[#002FA7] hover:underline" data-testid="inbound-address-copy">Copy</button>
            </div>
          </div>
        )}

        <div>
          <div className="text-sm font-medium text-[#171717] mb-1">Allowed senders</div>
          <p className="text-xs text-[#737373] mb-2">
            Only these email addresses can forward to your inbound address. Forwards from anyone else are silently ignored.
          </p>

          {/* Editable DB-backed list */}
          {dbSenders.length === 0 && envSenders.length === 0 ? (
            <div className="text-xs text-[#A3A3A3] italic mb-2">No senders configured yet.</div>
          ) : (
            <ul className="space-y-1 mb-3" data-testid="inbound-senders-list">
              {dbSenders.map((email) => (
                <li key={email} className="flex items-center gap-2 text-sm py-1.5 px-2.5 bg-[#FAFAFA] border border-[#E5E5E5]">
                  <span className="flex-1" data-testid={`inbound-sender-${email}`}>{email}</span>
                  <button
                    onClick={() => removeSender(email)}
                    className="text-xs text-[#DC2626] hover:underline"
                    data-testid={`inbound-sender-remove-${email}`}
                  >
                    Remove
                  </button>
                </li>
              ))}
              {envSenders.filter((e) => !dbSenders.includes(e)).map((email) => (
                <li key={email} className="flex items-center gap-2 text-sm py-1.5 px-2.5 bg-[#F5F5F5] border border-[#E5E5E5] text-[#737373]">
                  <span className="flex-1">{email}</span>
                  <span className="text-[10px] uppercase tracking-wide">env-locked</span>
                </li>
              ))}
            </ul>
          )}

          <div className="flex gap-2">
            <input
              type="email"
              value={newSender}
              onChange={(e) => setNewSender(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addSender(); } }}
              placeholder="someone@example.com"
              className="flex-1 px-3 py-2 border border-[#E5E5E5] focus:border-[#002FA7] focus:outline-none bg-white text-sm"
              data-testid="inbound-sender-input"
            />
            <button
              onClick={addSender}
              disabled={adding || !newSender.trim()}
              className="px-4 py-2 bg-[#0A0A0A] text-white text-sm font-medium hover:bg-[#171717] disabled:opacity-50"
              data-testid="inbound-sender-add"
            >
              {adding ? "Adding…" : "Add"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Webhook diagnostic — shows every inbound webhook hit so the owner can
// see exactly why a forwarded email did or didn't land. Owner-only.
function WebhookLogCard() {
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/inbound/webhook-log?limit=20");
      setRows(Array.isArray(data) ? data : []);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const statusTone = (s) => {
    if (s?.startsWith("ok")) return "bg-[#10B981]/10 text-[#047857] border-[#10B981]/40";
    if (s === "duplicate_skipped") return "bg-[#A3A3A3]/10 text-[#525252] border-[#A3A3A3]/40";
    return "bg-[#DC2626]/10 text-[#92400E] border-[#DC2626]/40";
  };
  const statusHelp = (s) => ({
    ok_attachments: "Successfully processed attachment(s) into rows.",
    ok_body: "Successfully extracted from email body (no attachments).",
    duplicate_skipped: "Postmark retried a message we already saw — no-op.",
    dropped_sender_not_allowed: "Sender's email is NOT in your Allowed Senders list above. Add them and re-forward.",
    no_mailbox_hash: "Email arrived without a +slug. You must send to <HASH>+northstar@inbound.postmarkapp.com (note the +northstar).",
    company_not_found: "The +slug on your email doesn't match any company's Inbound mailbox slug. Check the slug above matches.",
    no_attachments_no_body: "Email had no PDF and no readable body — nothing to extract.",
    body_unparseable: "Email body had no recognizable amount, invoice number, or party name. Forward as PDF for best results.",
    invalid_json: "Postmark sent a malformed payload (very rare).",
  })[s] || s;

  return (
    <div className="surface-card bg-white p-6 mb-6" data-testid="webhook-log-card">
      <div className="flex items-start justify-between mb-4 gap-3">
        <div>
          <div className="label-tag mb-1">Owner-only · Diagnostics</div>
          <h2 className="font-heading text-lg font-medium">Inbound webhook activity</h2>
          <p className="text-xs text-[#525252] mt-1 max-w-prose">
            Every email Postmark forwards lands here, success OR drop. If you forwarded an email and don&apos;t see it on the list at all, Postmark didn&apos;t deliver to this server (check Postmark&apos;s Activity tab — and confirm the webhook URL points to <span className="font-mono text-[10px] bg-[#FAFAF7] px-1.5 py-0.5 border border-[#E5E5E5]">{(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}/api/inbound/email</span>).
          </p>
        </div>
        <button
          onClick={load}
          className="text-xs px-3 py-1.5 border border-[#E5E5E5] hover:bg-[#FAFAF7] flex-shrink-0"
          data-testid="webhook-log-refresh"
          disabled={loading}
        >
          {loading ? "Loading…" : "Refresh"}
        </button>
      </div>
      {rows === null ? (
        <div className="text-sm text-[#737373]">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="border border-dashed border-[#E5E5E5] p-6 text-center" data-testid="webhook-log-empty">
          <div className="font-medium text-sm">No inbound webhook hits yet.</div>
          <div className="text-xs text-[#737373] mt-1 max-w-prose mx-auto">
            If Postmark says it received your email but nothing shows here, the webhook URL on the Postmark side is probably pointing somewhere else (e.g. your production deployment, a different env, or a typo).
          </div>
        </div>
      ) : (
        <ul className="space-y-2 text-sm" data-testid="webhook-log-list">
          {rows.map((r, i) => (
            <li
              key={r.id || `${r.received_at || ""}-${i}`}
              className="border border-[#E5E5E5] p-3 bg-white"
            >
              <div className="flex items-center gap-2 flex-wrap mb-1.5">
                <span className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider border ${statusTone(r.status)}`}>
                  {r.status}
                </span>
                <span className="text-xs text-[#737373] font-mono">{(r.received_at || "").slice(0, 19).replace("T", " ")}</span>
                {r.remit_count != null && (r.remit_count > 0 || r.expense_count > 0) && (
                  <span className="text-[10px] text-[#525252]">
                    {r.remit_count > 0 && `${r.remit_count} remittance${r.remit_count === 1 ? "" : "s"}`}
                    {r.remit_count > 0 && r.expense_count > 0 && ", "}
                    {r.expense_count > 0 && `${r.expense_count} expense${r.expense_count === 1 ? "" : "s"}`}
                  </span>
                )}
              </div>
              <div className="text-xs text-[#171717]">
                <span className="text-[#737373]">From:</span> {r.from || "(unknown)"}
                {" · "}
                <span className="text-[#737373]">Subject:</span> {r.subject || "(none)"}
              </div>
              <div className="text-xs text-[#737373] mt-0.5 font-mono">
                hash: {r.mailbox_hash || "(empty — missing +slug)"} · attachments: {r.attachment_count || 0} · body: {r.had_body ? "yes" : "no"}
              </div>
              <div className="text-xs text-[#525252] mt-1.5 italic">{statusHelp(r.status)}</div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// Inquiry health widget — owner-only. Tracks resolution speed across companies.
function InquiryHealthCard() {
  const { activeCompanyId } = useCompany();
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!activeCompanyId) return;
    api.get(`/admin/inquiry-health?company_id=${activeCompanyId}`).then(({ data }) => setData(data)).catch(() => {});
  }, [activeCompanyId]);

  if (!data) return null;

  const oldest = data.oldest_open;

  return (
    <div className="surface-card bg-white p-6 mb-6" data-testid="inquiry-health-card">
      <div className="mb-4">
        <div className="label-tag mb-1">Owner-only</div>
        <h2 className="font-heading text-lg font-medium">Inquiry health</h2>
        <p className="text-sm text-[#525252] mt-1">
          How quickly the bookkeeper&rsquo;s questions get resolved. Slow trends mean she&rsquo;s waiting on you (or the data).
        </p>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        <Stat label="Open" value={data.unresolved} />
        <Stat label="Resolved" value={data.resolved} hint="all-time" />
        <Stat
          label="Avg days to resolve"
          value={data.avg_days_to_resolve_6mo == null ? "—" : `${data.avg_days_to_resolve_6mo}d`}
          hint="last 6 months"
        />
        <Stat
          label="Oldest open"
          value={oldest?.age_days != null ? `${oldest.age_days}d` : "—"}
          hint={oldest?.title ? oldest.title.slice(0, 40) : "nothing"}
        />
      </div>
      {data.trend && data.trend.length > 0 && (
        <div>
          <div className="label-tag text-[10px] mb-2">Avg days to resolve · by close month</div>
          <div className="flex items-end gap-2 h-24">
            {data.trend.map((t) => {
              const max = Math.max(...data.trend.map((r) => r.avg_days || 0), 1);
              const h = ((t.avg_days || 0) / max) * 100;
              return (
                <div key={t.month} className="flex-1 flex flex-col items-center gap-1" data-testid={`trend-${t.month}`}>
                  <div className="text-[10px] text-[#525252] tabular-nums">{t.avg_days != null ? `${t.avg_days}d` : "—"}</div>
                  <div className="w-full bg-[#002FA7]/15 relative h-16 flex items-end">
                    <div className="w-full bg-[#002FA7]" style={{ height: `${h}%` }} />
                  </div>
                  <div className="text-[10px] text-[#525252]">{t.month.slice(5)}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, hint }) {
  return (
    <div className="bg-[#FAFAFA] p-3 border border-[#E5E5E5]">
      <div className="label-tag text-[10px] mb-1">{label}</div>
      <div className="font-heading text-2xl font-medium leading-none text-[#0A0A0A]">{value}</div>
      {hint && <div className="text-[10px] text-[#525252] mt-1 truncate" title={hint}>{hint}</div>}
    </div>
  );
}

function AdoptionTrackingCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [windowDays, setWindowDays] = useState(30);
  const [expanded, setExpanded] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/adoption-stats?days=${windowDays}`);
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load adoption stats");
    }
    setLoading(false);
  }, [windowDays]);

  useEffect(() => { load(); }, [load]);

  const trendBadge = (trend) => {
    const map = {
      up: { label: "Trending up", color: "#10B981", bg: "#D1FAE5" },
      steady: { label: "Steady", color: "#525252", bg: "#F5F5F5" },
      down: { label: "Slowing down", color: "#F59E0B", bg: "#FEF3C7" },
      stopped: { label: "Stopped using", color: "#DC2626", bg: "#FEE2E2" },
      newly_active: { label: "Newly active", color: "#002FA7", bg: "#DBEAFE" },
      no_activity: { label: "Not using portal", color: "#DC2626", bg: "#FEE2E2" },
    };
    const m = map[trend] || map.steady;
    return (
      <span
        className="text-[10px] font-semibold px-2 py-0.5"
        style={{ color: m.color, backgroundColor: m.bg }}
        data-testid={`adoption-trend-${trend}`}
      >
        {m.label}
      </span>
    );
  };

  return (
    <section className="surface-card bg-white" data-testid="adoption-tracking-card">
      <div className="p-5 border-b border-[#E5E5E5] flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-[#002FA7] flex items-center justify-center">
            <ListChecks size={18} weight="duotone" className="text-white" />
          </div>
          <div>
            <h2 className="font-heading text-lg font-medium">Portal adoption (owner-only)</h2>
            <p className="text-xs text-[#525252] max-w-md">
              Track how thoroughly each team member is using the new workflow. <b>This card is invisible to bookkeeper and staff</b> — only you can see it.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={windowDays}
            onChange={(e) => setWindowDays(Number(e.target.value))}
            className="text-xs px-2 py-1 border border-[#E5E5E5] bg-white"
            data-testid="adoption-window-select"
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={60}>Last 60 days</option>
            <option value={90}>Last 90 days</option>
          </select>
          <button
            onClick={load}
            disabled={loading}
            className="text-xs px-2 py-1 border border-[#E5E5E5] hover:bg-[#FAFAFA] disabled:opacity-50"
            data-testid="adoption-refresh"
          >
            {loading ? "Loading…" : "Refresh"}
          </button>
        </div>
      </div>

      {data && data.users.length === 0 && (
        <div className="p-6 text-center text-sm text-[#525252]">No users yet.</div>
      )}

      <div className="divide-y divide-[#E5E5E5]">
        {data?.users.map((row) => {
          const isExpanded = !!expanded[row.user_id];
          const dotColor = row.trend === "up" || row.trend === "newly_active" ? "#10B981"
            : row.trend === "stopped" || row.trend === "no_activity" ? "#DC2626"
            : row.trend === "down" ? "#F59E0B"
            : "#A3A3A3";
          return (
            <div key={row.user_id} data-testid={`adoption-row-${row.user_id}`}>
              <button
                onClick={() => setExpanded((m) => ({ ...m, [row.user_id]: !m[row.user_id] }))}
                className="w-full p-4 flex items-center justify-between gap-3 hover:bg-[#FAFAFA] transition-colors text-left"
                data-testid={`adoption-row-toggle-${row.user_id}`}
              >
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: dotColor }} />
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-sm truncate">{row.user_name}</div>
                    <div className="text-[11px] text-[#525252] truncate">{row.user_email} · {row.user_role}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3 text-xs flex-shrink-0">
                  <div className="text-right hidden sm:block">
                    <div className="font-mono font-bold text-base leading-none">{row.total_current}</div>
                    <div className="text-[10px] text-[#A3A3A3]">portal events</div>
                  </div>
                  <div className="text-right hidden md:block">
                    <div className="text-[#525252]">{row.total_previous}</div>
                    <div className="text-[10px] text-[#A3A3A3]">prior {windowDays}d</div>
                  </div>
                  {trendBadge(row.trend)}
                  <CaretRight size={14} weight="bold" className={`text-[#A3A3A3] transition-transform ${isExpanded ? "rotate-90" : ""}`} />
                </div>
              </button>
              {isExpanded && (
                <div className="px-4 pb-4 pt-1 bg-[#FAFAF7] border-t border-[#F5F5F5] space-y-3" data-testid={`adoption-detail-${row.user_id}`}>
                  <div className="text-[11px] text-[#525252]">
                    Last portal action: <b>{row.last_active ? new Date(row.last_active).toLocaleString() : "Never in this window"}</b>
                  </div>
                  <div>
                    <div className="label-tag mb-2">Per-category breakdown ({windowDays}-day window)</div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                      {data.categories.map((c) => {
                        const cat = row.categories[c.key] || { current: 0, previous: 0 };
                        const delta = cat.current - cat.previous;
                        return (
                          <div key={c.key} className="flex items-center justify-between bg-white px-3 py-2 border border-[#F5F5F5]">
                            <span className="text-[12px] text-[#333]">{c.label}</span>
                            <div className="flex items-center gap-2 font-mono">
                              <span className="font-bold text-sm">{cat.current}</span>
                              {cat.previous > 0 && (
                                <span className="text-[10px] text-[#A3A3A3]">prev {cat.previous}</span>
                              )}
                              {delta !== 0 && (
                                <span className={`text-[10px] font-semibold ${delta > 0 ? "text-[#10B981]" : "text-[#F59E0B]"}`}>
                                  {delta > 0 ? "+" : ""}{delta}
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  {Object.keys(row.per_company).length > 0 && (
                    <div>
                      <div className="label-tag mb-2">Activity by company</div>
                      <div className="flex flex-wrap gap-2">
                        {Object.entries(row.per_company).map(([cid, count]) => (
                          <span key={cid} className="text-[11px] px-2 py-1 bg-white border border-[#E5E5E5]">
                            <b>{data.company_lookup[cid] || cid}</b>: {count}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div className="p-3 bg-[#FAFAFA] border-t border-[#E5E5E5] text-[11px] text-[#525252] italic">
        Tracking is collected from the portal&apos;s activity log. Actions performed directly inside QuickBooks (bypassing the portal) do <b>not</b> appear here — that itself is a useful adoption signal: a low count combined with knowing the work got done means they&apos;re working around the portal.
      </div>
    </section>
  );
}

function WorkflowFunnelCard() {
  const { companies } = useCompany();
  const [companyId, setCompanyId] = useState("");
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  // Default to the first company
  useEffect(() => {
    if (!companyId && companies && companies.length > 0) {
      setCompanyId(companies[0].id);
    }
  }, [companies, companyId]);

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/workflow-funnel?company_id=${companyId}&days=${days}`);
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load workflow funnel");
    }
    setLoading(false);
  }, [companyId, days]);

  useEffect(() => { load(); }, [load]);

  const stepColor = (step) => {
    // Red if drop-off > 30% of previous step, amber 10-30%, green <10%
    if (step.step === 1) return "#10B981"; // upload baseline always green
    const prev = data?.steps?.[step.step - 2];
    if (!prev || prev.portal_count === 0) return "#A3A3A3";
    const dropPct = (step.drop_off_from_prev / prev.portal_count) * 100;
    if (dropPct >= 30) return "#DC2626";
    if (dropPct >= 10) return "#F59E0B";
    return "#10B981";
  };

  return (
    <section className="surface-card bg-white" data-testid="workflow-funnel-card">
      <div className="p-5 border-b border-[#E5E5E5] flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-[#002FA7] flex items-center justify-center">
            <ListChecks size={18} weight="duotone" className="text-white" />
          </div>
          <div>
            <h2 className="font-heading text-lg font-medium">Deposit workflow funnel (owner-only)</h2>
            <p className="text-xs text-[#525252] max-w-xl">
              Every step of the deposit workflow with drop-off measurement. Compare portal-completed work against QuickBooks totals to detect bypass.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select
            value={companyId}
            onChange={(e) => setCompanyId(e.target.value)}
            className="text-xs px-2 py-1 border border-[#E5E5E5] bg-white max-w-[200px]"
            data-testid="funnel-company-select"
          >
            {(companies || []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="text-xs px-2 py-1 border border-[#E5E5E5] bg-white"
            data-testid="funnel-window-select"
          >
            <option value={7}>7d</option>
            <option value={30}>30d</option>
            <option value={60}>60d</option>
            <option value={90}>90d</option>
          </select>
          <button
            onClick={load}
            disabled={loading}
            className="text-xs px-2 py-1 border border-[#E5E5E5] hover:bg-[#FAFAFA] disabled:opacity-50"
            data-testid="funnel-refresh"
          >
            {loading ? "Loading…" : "Refresh"}
          </button>
        </div>
      </div>

      {data && (
        <div className="p-5 space-y-5">
          {/* Bookkeeper hours saved — what this all adds up to in real money */}
          {data.time_saved && data.time_saved.minutes_total > 0 && (
            <div className="bg-[#10B981]/[0.06] border-l-2 border-[#10B981] p-3 flex items-center justify-between gap-3 flex-wrap" data-testid="funnel-time-saved">
              <div>
                <div className="label-tag mb-1 text-[#047857]">Estimated bookkeeper hours saved this window</div>
                <div className="font-mono text-3xl font-bold text-[#047857]">
                  {data.time_saved.hours_total}h
                  <span className="text-sm font-normal text-[#525252] ml-2">
                    ({data.time_saved.minutes_total} minutes)
                  </span>
                </div>
              </div>
              <details className="text-[11px] text-[#525252] max-w-sm">
                <summary className="cursor-pointer">How this is estimated</summary>
                <div className="mt-1 leading-relaxed">{data.time_saved.assumptions}</div>
                <div className="mt-1 grid grid-cols-2 gap-x-3 gap-y-0.5">
                  <span>Auto-fill:</span><b className="font-mono">{data.time_saved.breakdown.auto_filled_min}m</b>
                  <span>Find matches:</span><b className="font-mono">{data.time_saved.breakdown.matched_min}m</b>
                  <span>Apply:</span><b className="font-mono">{data.time_saved.breakdown.applied_min}m</b>
                  <span>Clear:</span><b className="font-mono">{data.time_saved.breakdown.cleared_min}m</b>
                  <span>Post deposit:</span><b className="font-mono">{data.time_saved.breakdown.deposit_posted_min}m</b>
                </div>
              </details>
            </div>
          )}

          {/* Funnel — visual horizontal bars */}
          <div>
            <div className="label-tag mb-3">{data.company_name} · last {data.window_days} days</div>
            <div className="space-y-2">
              {data.steps.map((s) => {
                const pct = s.of_uploaded_pct;
                const color = stepColor(s);
                const dropPct = s.step > 1 && data.steps[s.step - 2].portal_count > 0
                  ? Math.round((s.drop_off_from_prev / data.steps[s.step - 2].portal_count) * 100)
                  : 0;
                return (
                  <div key={s.key} className="space-y-1" data-testid={`funnel-step-${s.key}`}>
                    <div className="flex items-baseline justify-between gap-3 text-sm">
                      <div className="flex items-center gap-2 min-w-0">
                        <span
                          className="w-5 h-5 flex items-center justify-center text-[10px] font-bold text-white rounded-full flex-shrink-0"
                          style={{ backgroundColor: color }}
                        >{s.step}</span>
                        <span className="font-medium truncate">{s.label}</span>
                      </div>
                      <div className="flex items-center gap-3 font-mono text-xs flex-shrink-0">
                        {s.step > 1 && s.drop_off_from_prev > 0 && (
                          <span className="text-[#DC2626]" title="Dropped off vs. previous step">
                            -{s.drop_off_from_prev} ({dropPct}%)
                          </span>
                        )}
                        <span className="font-bold text-base">{s.portal_count}</span>
                        <span className="text-[#A3A3A3] text-[10px] w-12 text-right">{pct}% of #1</span>
                      </div>
                    </div>
                    <div className="bg-[#F5F5F5] h-2 relative">
                      <div
                        className="h-full transition-all"
                        style={{ width: `${pct}%`, backgroundColor: color }}
                      />
                    </div>
                    <div className="text-[11px] text-[#525252] italic pl-7">
                      {s.what_it_means}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* QBO bypass comparison */}
          {data.qbo_connected ? (
            data.qbo_comparison ? (
              <div className="border-t border-[#E5E5E5] pt-4">
                <div className="label-tag mb-3 flex items-center gap-1.5">
                  <Sparkle size={10} weight="duotone" /> Portal vs. QuickBooks (bypass detection)
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <BypassPanel
                    title="QBO Payments"
                    total={data.qbo_comparison.qbo_payments_total}
                    via={data.qbo_comparison.qbo_payments_via_portal}
                    direct={data.qbo_comparison.qbo_payments_direct}
                    sharePct={data.qbo_comparison.payment_portal_share_pct}
                    examples={data.qbo_comparison.direct_payment_examples}
                    examplesLabel="Direct (bypassed portal)"
                  />
                  <BypassPanel
                    title="QBO Deposits"
                    total={data.qbo_comparison.qbo_deposits_total}
                    via={data.qbo_comparison.qbo_deposits_via_portal}
                    direct={data.qbo_comparison.qbo_deposits_direct}
                    sharePct={data.qbo_comparison.deposit_portal_share_pct}
                  />
                </div>
              </div>
            ) : data.qbo_error ? (
              <div className="border-t border-[#E5E5E5] pt-4 text-xs text-[#F59E0B]">
                Couldn&apos;t reach QuickBooks for the bypass comparison: {data.qbo_error}
              </div>
            ) : null
          ) : (
            <div className="border-t border-[#E5E5E5] pt-4 text-xs text-[#525252] italic">
              Connect QuickBooks for this entity to enable bypass detection (compares portal-completed work against QBO totals).
            </div>
          )}

          {/* Per-user breakdown */}
          {data.by_user.length > 0 && (
            <div className="border-t border-[#E5E5E5] pt-4">
              <div className="label-tag mb-3">By user</div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="funnel-by-user-table">
                  <thead>
                    <tr className="text-left text-[#525252] border-b border-[#E5E5E5]">
                      <th className="py-2 pr-3">User</th>
                      <th className="py-2 px-2 text-right">Uploaded</th>
                      <th className="py-2 px-2 text-right">Auto-fill</th>
                      <th className="py-2 px-2 text-right">Matched</th>
                      <th className="py-2 px-2 text-right">Applied</th>
                      <th className="py-2 px-2 text-right">Cleared</th>
                      <th className="py-2 px-2 text-right">Deposit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.by_user.map((u) => (
                      <tr key={u.user_id} className="border-b border-[#F5F5F5]" data-testid={`funnel-user-row-${u.user_id}`}>
                        <td className="py-2 pr-3 font-medium">{u.user_name}</td>
                        <td className="py-2 px-2 text-right font-mono">{u.uploaded}</td>
                        <td className="py-2 px-2 text-right font-mono">{u.auto_filled}</td>
                        <td className="py-2 px-2 text-right font-mono">{u.matched}</td>
                        <td className="py-2 px-2 text-right font-mono">{u.applied}</td>
                        <td className="py-2 px-2 text-right font-mono">{u.cleared}</td>
                        <td className="py-2 px-2 text-right font-mono">{u.deposited}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
      <div className="p-3 bg-[#FAFAFA] border-t border-[#E5E5E5] text-[11px] text-[#525252] italic">
        How to read this: green steps = healthy adoption · amber = some skipping · red = serious drop-off (most likely bypass). The QuickBooks comparison shows what slipped past the portal entirely.
      </div>
    </section>
  );
}

function BypassPanel({ title, total, via, direct, sharePct, examples, examplesLabel }) {
  const color = sharePct >= 80 ? "#10B981" : sharePct >= 50 ? "#F59E0B" : "#DC2626";
  return (
    <div className="bg-[#FAFAF7] border-l-2 p-3" style={{ borderLeftColor: color }} data-testid={`bypass-panel-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="font-heading font-semibold text-sm mb-2">{title} ({total})</div>
      <div className="flex items-center gap-2 mb-2">
        <span className="font-bold text-2xl font-mono" style={{ color }}>{sharePct}%</span>
        <span className="text-[11px] text-[#525252] leading-tight">via portal</span>
      </div>
      <div className="text-[11px] space-y-0.5">
        <div className="flex justify-between"><span>Created via portal</span><b className="font-mono">{via}</b></div>
        <div className="flex justify-between"><span className="text-[#DC2626]">Created directly in QBO</span><b className="font-mono text-[#DC2626]">{direct}</b></div>
      </div>
      {examples && examples.length > 0 && (
        <details className="mt-2">
          <summary className="text-[11px] cursor-pointer text-[#525252] hover:text-[#0A0A0A]">
            Show {examplesLabel || "direct examples"}
          </summary>
          <ul className="mt-1.5 space-y-0.5 text-[11px] font-mono bg-white p-2 border border-[#F5F5F5]">
            {examples.map((p) => (
              <li key={p.id} className="flex justify-between">
                <span>{p.date} · {p.customer || "—"}</span>
                <b>${Number(p.amount || 0).toFixed(2)}</b>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

function RuleEffectivenessCard() {
  const { companies } = useCompany();
  const [companyId, setCompanyId] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(null);

  useEffect(() => {
    if (!companyId && companies && companies.length > 0) {
      setCompanyId(companies[0].id);
    }
  }, [companies, companyId]);

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/rule-effectiveness?company_id=${companyId}&days=90`);
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load rule effectiveness");
    }
    setLoading(false);
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  const applyRecommendation = async (rule) => {
    if (!rule.recommendation || !rule.recommendation.proposed_value) return;
    if (!window.confirm(`Update Rule B confidence threshold from ${rule.recommendation.current_value}% to ${rule.recommendation.proposed_value}%?\n\nThis change is logged and you can revert any time.`)) return;
    setApplying(rule.key);
    try {
      await api.post(`/admin/rule-threshold`, { company_id: companyId, value: rule.recommendation.proposed_value });
      toast.success(`Threshold updated to ${rule.recommendation.proposed_value}%`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update threshold");
    }
    setApplying(null);
  };

  const resetThreshold = async () => {
    if (!window.confirm("Reset Rule B confidence threshold to default (80%)?")) return;
    try {
      await api.post(`/admin/rule-threshold`, { company_id: companyId, value: 80 });
      toast.success("Threshold reset to 80%");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to reset threshold");
    }
  };

  return (
    <section className="surface-card bg-white" data-testid="rule-effectiveness-card">
      <div className="p-5 border-b border-[#E5E5E5] flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-[#002FA7] flex items-center justify-center">
            <Sparkle size={18} weight="duotone" className="text-white" />
          </div>
          <div>
            <h2 className="font-heading text-lg font-medium">Auto-flag rule self-tuning (owner-only)</h2>
            <p className="text-xs text-[#525252] max-w-xl">
              The portal watches what the bookkeeper does with each flagged remittance and recommends rule changes when the data supports it. No input from her required.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select
            value={companyId}
            onChange={(e) => setCompanyId(e.target.value)}
            className="text-xs px-2 py-1 border border-[#E5E5E5] bg-white max-w-[200px]"
            data-testid="rule-eff-company-select"
          >
            {(companies || []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <button
            onClick={load}
            disabled={loading}
            className="text-xs px-2 py-1 border border-[#E5E5E5] hover:bg-[#FAFAFA] disabled:opacity-50"
            data-testid="rule-eff-refresh"
          >
            {loading ? "Loading…" : "Refresh"}
          </button>
        </div>
      </div>

      {data && (
        <div className="p-5 space-y-5">
          <div className="bg-[#F5F5F5] p-3 flex items-center justify-between flex-wrap gap-2">
            <div className="text-xs">
              <div className="label-tag mb-0.5">Current Rule-B confidence threshold</div>
              <div className="font-mono text-2xl font-bold">{data.current_threshold}%</div>
              {data.threshold_updated_at && (
                <div className="text-[11px] text-[#525252]">
                  Last changed: {new Date(data.threshold_updated_at).toLocaleString()} by {data.threshold_updated_by || "owner"}
                </div>
              )}
            </div>
            {data.current_threshold !== 80 && (
              <button
                onClick={resetThreshold}
                className="text-[11px] px-2 py-1 border border-[#E5E5E5] hover:bg-white"
                data-testid="rule-eff-reset"
              >
                Reset to default (80%)
              </button>
            )}
          </div>

          <div className="space-y-3">
            {Object.values(data.rules).map((rule) => {
              const total = rule.applied_count;
              const agreementColor = rule.agreement_rate_pct === null
                ? "#A3A3A3"
                : rule.agreement_rate_pct >= 85 ? "#F59E0B"
                : rule.agreement_rate_pct >= 50 ? "#525252"
                : "#10B981";
              return (
                <div key={rule.key} className="border border-[#E5E5E5] p-3" data-testid={`rule-row-${rule.key}`}>
                  <div className="flex items-baseline justify-between gap-3 flex-wrap mb-1">
                    <div className="font-heading font-semibold text-sm">{rule.label}</div>
                    <div className="flex items-center gap-3 text-[11px]">
                      <span><b>{rule.fired_count}</b> fires</span>
                      <span><b>{rule.applied_count}</b> resolved</span>
                      {total > 0 && (
                        <span style={{ color: agreementColor }}>
                          <b>{rule.agreed_count}</b> agreed · <b>{rule.modified_count}</b> modified
                          {rule.split_count > 0 && <> · <b>{rule.split_count}</b> split</>}
                        </span>
                      )}
                      {rule.agreement_rate_pct !== null && (
                        <span className="font-mono font-bold" style={{ color: agreementColor }}>
                          {rule.agreement_rate_pct}% agreement
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="text-[11px] text-[#525252] mb-2">{rule.description}</div>
                  {rule.recommendation && (
                    <div className="bg-[#FEF3C7] border-l-2 border-[#F59E0B] p-2.5 mt-2 flex items-start justify-between gap-3 flex-wrap">
                      <div className="text-[12px] text-[#78350F] flex-1 min-w-0 leading-relaxed">
                        <div className="font-heading font-semibold mb-0.5">Recommendation</div>
                        {rule.recommendation.summary}
                      </div>
                      {rule.tunable && rule.recommendation.proposed_value && (
                        <button
                          onClick={() => applyRecommendation(rule)}
                          disabled={applying === rule.key}
                          className="text-[11px] px-3 py-1.5 bg-[#F59E0B] text-white hover:bg-[#D97706] disabled:opacity-60 flex items-center gap-1 flex-shrink-0"
                          data-testid={`rule-apply-rec-${rule.key}`}
                        >
                          {applying === rule.key ? "…" : `Set to ${rule.recommendation.proposed_value}%`}
                          <ArrowRight size={11} weight="bold" />
                        </button>
                      )}
                    </div>
                  )}
                  {!rule.recommendation && total < 10 && rule.fired_count > 0 && (
                    <div className="text-[11px] text-[#A3A3A3] italic">
                      Need {10 - total} more resolved flag{10 - total > 1 ? "s" : ""} before recommending changes (need ≥10 for stat. significance).
                    </div>
                  )}
                  {!rule.recommendation && rule.fired_count === 0 && (
                    <div className="text-[11px] text-[#A3A3A3] italic">
                      Rule hasn&apos;t fired in this window — no data yet.
                    </div>
                  )}
                  {rule.recent_examples && rule.recent_examples.length > 0 && (
                    <details className="mt-2">
                      <summary className="cursor-pointer text-[11px] text-[#525252] hover:text-[#0A0A0A]">
                        Show recent {rule.recent_examples.length} example{rule.recent_examples.length > 1 ? "s" : ""}
                      </summary>
                      <ul className="mt-1.5 space-y-0.5 text-[11px] bg-[#FAFAFA] p-2 border border-[#F5F5F5]">
                        {rule.recent_examples.map((ex) => (
                          <li key={ex.id} className="flex items-center justify-between gap-2 font-mono">
                            <span className="truncate">
                              {ex.review_set_at ? new Date(ex.review_set_at).toLocaleDateString() : "—"} · {ex.payer_name || "—"} · ${Number(ex.amount || 0).toFixed(2)}
                            </span>
                            <span className="flex-shrink-0 text-[10px]">
                              {ex.review_outcome === "agreed" && <span className="text-[#F59E0B]">agreed</span>}
                              {ex.review_outcome === "modified" && <span className="text-[#10B981]">modified ✓</span>}
                              {ex.review_outcome === "split" && <span className="text-[#10B981]">split ✓</span>}
                              {!ex.review_outcome && !ex.qbo_payment_id && <span className="text-[#A3A3A3]">unresolved</span>}
                              {!ex.review_outcome && ex.qbo_payment_id && <span className="text-[#A3A3A3]">applied (no diff)</span>}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </details>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
      <div className="p-3 bg-[#FAFAFA] border-t border-[#E5E5E5] text-[11px] text-[#525252] italic">
        <b>How this works (silent observation):</b> when the bookkeeper applies a flagged remittance, we compare what she actually applied against what the algorithm originally suggested. If she agreed (no edits), the flag was probably over-cautious. If she changed something, the flag saved a real mistake. After ≥10 resolved flags, we recommend a tuning change you can apply with one click.
      </div>
    </section>
  );
}

